import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import type { DefaultMatchRequirementMapping } from 'dashboard/models/verification-check';
import {
  VerificationTemplate,
  VerificationTemplateBuilders,
  VerificationTemplateStatus,
} from 'dashboard/models/verification-template';
import Action from 'lib/interfaces/Action';
import { PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export type VerificationTemplatesActions =
  | Action<
      VerificationTemplatesActionType.SetVerificationTemplates,
      SetVerificationTemplatesPayload
    >
  | Action<VerificationTemplatesActionType.SetCurrentPage, SetCurrentPagePayload>
  | Action<VerificationTemplatesActionType.SetPageSize, SetPageSizePayload>
  | Action<VerificationTemplatesActionType.SetFilters, SetFiltersPayload>
  | Action<VerificationTemplatesActionType.SetSortParams, SetSortParamsPayload>
  | Action<
      VerificationTemplatesActionType.SetDefaultMatchRequirements,
      SetDefaultMatchRequirementsPayload
    >
  | Action<
      VerificationTemplatesActionType.SetVerificationTemplateBuilders,
      SetVerificationTemplateBuildersPayload
    >;

export enum VerificationTemplatesActionType {
  SetVerificationTemplates = 'verification-templates/SET_VERIFICATION_TEMPLATES',
  SetCurrentPage = 'verification-templates/SET_CURRENT_PAGE',
  SetPageSize = 'verification-templates/SET_PAGE_SIZE',
  SetFilters = 'verification-templates/SET_FILTERS',
  SetSortParams = 'verification-templates/SET_SORT_PARAMS',
  SetDefaultMatchRequirements = 'verification-templates/SET_DEFAULT_CHECK_REQUIREMENTS',
  SetVerificationTemplateBuilders = 'verification-templates/SET_VERIFICATION_TEMPLATE_BUILDERS',
}

interface SetVerificationTemplatesPayload {
  verificationTemplates: VerificationTemplate[];
  paginationParams: PaginationParams;
}

export function setVerificationTemplates(
  verificationTemplates: VerificationTemplate[],
  paginationParams: PaginationParams,
): Action<
  VerificationTemplatesActionType.SetVerificationTemplates,
  SetVerificationTemplatesPayload
> {
  return {
    payload: {
      verificationTemplates,
      paginationParams,
    },
    type: VerificationTemplatesActionType.SetVerificationTemplates,
  };
}

interface SetCurrentPagePayload {
  currentPage: number;
}

export function setCurrentPage(
  currentPage: number,
): Action<VerificationTemplatesActionType.SetCurrentPage, SetCurrentPagePayload> {
  return {
    payload: {
      currentPage,
    },
    type: VerificationTemplatesActionType.SetCurrentPage,
  };
}

interface SetPageSizePayload {
  pageSize: number;
}

export function setPageSize(
  pageSize: number,
): Action<VerificationTemplatesActionType.SetPageSize, SetPageSizePayload> {
  return {
    payload: {
      pageSize,
    },
    type: VerificationTemplatesActionType.SetPageSize,
  };
}

export interface VerificationTemplatesFilterParams {
  createdAtStart?: Iso8601DateString;
  createdAtEnd?: Iso8601DateString;
  omni?: string;
  status?: VerificationTemplateStatus[];
  type?: VerificationTemplate['type'][];
}

interface SetFiltersPayload {
  filters: VerificationTemplatesFilterParams;
}

export function setFilters(
  filters: VerificationTemplatesFilterParams,
): Action<VerificationTemplatesActionType.SetFilters, SetFiltersPayload> {
  return {
    payload: {
      filters,
    },
    type: VerificationTemplatesActionType.SetFilters,
  };
}

interface SetSortParamsPayload {
  sortParams: SortParams;
}

export function setSortParams(
  sortParams: SortParams,
): Action<VerificationTemplatesActionType.SetSortParams, SetSortParamsPayload> {
  return {
    payload: {
      sortParams,
    },
    type: VerificationTemplatesActionType.SetSortParams,
  };
}

interface SetDefaultMatchRequirementsPayload {
  defaultMatchRequirements: DefaultMatchRequirementMapping;
}

export function setDefaultMatchRequirements(
  defaultMatchRequirements: DefaultMatchRequirementMapping,
): Action<
  VerificationTemplatesActionType.SetDefaultMatchRequirements,
  SetDefaultMatchRequirementsPayload
> {
  return {
    payload: {
      defaultMatchRequirements,
    },
    type: VerificationTemplatesActionType.SetDefaultMatchRequirements,
  };
}

interface SetVerificationTemplateBuildersPayload {
  verificationTemplateBuilders: VerificationTemplateBuilders;
}

export function setVerificationTemplateBuilders(
  verificationTemplateBuilders: VerificationTemplateBuilders,
): Action<
  VerificationTemplatesActionType.SetVerificationTemplateBuilders,
  SetVerificationTemplateBuildersPayload
> {
  return {
    payload: {
      verificationTemplateBuilders,
    },
    type: VerificationTemplatesActionType.SetVerificationTemplateBuilders,
  };
}
